import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, ButtonContainer, ContentSection, Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Formik, FormikContextType, useFormikContext } from 'formik';
import { ObjectSchema } from 'yup';
import { Yup } from '../../../../services/common/validation';
import { creditApplicationSelectors } from '../../../../services/redux/features/creditApplication.slice';
import { loadingActions } from '../../../../services/redux/features/loading.slice';
import { useNavigate } from '../../../../services/routing/navigate';
import { CustomerInCompanyFields, IsCustomerHasCompany } from '../../utils/fieldsPrivateCustomer';
import {
  CustomerDetailsFormGroupForms,
  CustomerDetailsFormGroupType,
  patchCreditApplicationAndUpdateState,
  useFormSectionHelpers,
} from '../utils';
import { CompanyDataFormType, getCompanyDataFormInitialValues } from './initialValues';
import { companyDataFormRequestMapper } from './requestMapper';
import { getCompanyDataFormValidationSchema } from './validationSchema';

interface Props {
  formName: CustomerDetailsFormGroupForms<'private'>;
  formGroup: CustomerDetailsFormGroupType;
}

export const CompanyDataForm: FunctionComponent<Props> = ({ formName, formGroup }) => {
  const [initialValues, setInitialValues] = useState({} as CompanyDataFormType);
  const [validationSchema, setValidationSchema] = useState<Yup.Lazy<ObjectSchema<CompanyDataFormType>>>();

  const creditApplicantCustomerData = useSelector(creditApplicationSelectors.selectCreditApplicantCustomerData) as
    | CreditIntegration.Schemas.UpdateLegalEntityCustomerDataResponse
    | CreditIntegration.Schemas.UpdateSoleTraderCustomerDataResponse;

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    setValidationSchema(getCompanyDataFormValidationSchema(t));
  }, [t]);

  useEffect(() => {
    setInitialValues(getCompanyDataFormInitialValues(creditApplicantCustomerData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creditApplicantCustomerData]);

  const { formSectionProps, onFormSubmitSuccess, onFormInitialValuesChange } = useFormSectionHelpers(
    formGroup,
    formName
  );

  const onSubmit = async (values: CompanyDataFormType) => {
    dispatch(loadingActions.setLoading(true));
    patchCreditApplicationAndUpdateState(companyDataFormRequestMapper(values, creditApplicantCustomerData))
      .then(onFormSubmitSuccess)
      .catch(navigate.error)
      .finally(() => dispatch(loadingActions.setLoading(false)));
  };

  const isCreditApplicationDataReady = useSelector(creditApplicationSelectors.selectIsCreditApplicationDataReady);

  return (
    <ContentSection.ComponentWrapper>
      <FormSection title={t('step4:heading:companyInformation')} {...formSectionProps}>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {isCreditApplicationDataReady && (
            /* eslint-disable-next-line no-use-before-define */
            <FormContent onFormInitialValuesChange={onFormInitialValuesChange} />
          )}
        </Formik>
      </FormSection>
    </ContentSection.ComponentWrapper>
  );
};

interface FormContentProps {
  onFormInitialValuesChange: (validateForm: FormikContextType<any>['validateForm']) => void;
}

const FormContent: FunctionComponent<FormContentProps> = ({ onFormInitialValuesChange }) => {
  const { setFieldValue, initialValues, validateForm, submitForm } = useFormikContext<CompanyDataFormType>();
  const { t } = useTranslation();

  useEffect(() => {
    onFormInitialValuesChange(validateForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  return (
    <Fieldset className="u-pt-none">
      <Fieldset.Row>
        <Layout>
          <Layout.Item default="1/2" s="1/1">
            <IsCustomerHasCompany t={t} setFieldValue={setFieldValue} />
          </Layout.Item>
          <Layout.Item default="1/2" s="1/1" className="u-hide@s" />
          <CustomerInCompanyFields t={t} setFieldValue={setFieldValue} />
          <ButtonContainer right>
            <Layout right>
              <Layout.Item default="1/3" m="2/5" s="2/3">
                <Button full type="submit" onClick={submitForm}>
                  {t('step3:navigation:continue')}
                </Button>
              </Layout.Item>
            </Layout>
          </ButtonContainer>
        </Layout>
      </Fieldset.Row>
    </Fieldset>
  );
};
